import React, { useEffect, useState } from "react";
import { Button, Modal, Select, SelectItem } from "carbon-components-react";
import { ChevronSort16 } from "@carbon/icons-react";

import { ColumnDef, HydratedSortAction, Schema, SortOrderKind } from "../shared";

import getSortAction from "./actions";

const styles = require("./styles.module.scss");

const columnOptionsFromSchema = (s: Schema): { value: string; label: string }[] =>
  s.map((d: ColumnDef) => ({ value: d.jsonKey, label: d.displayName }));

const sortOrderOptions = [SortOrderKind.Ascending, SortOrderKind.Descending].map(o => ({
  value: o,
  label: o,
}));

interface Props {
  schema: Schema;
  addAction: Function;

  actionToUpdate: HydratedSortAction;
  updateAction: Function;
  openModal: boolean;
}

const resolveJsonKey = (schema: Schema, actionToUpdate: HydratedSortAction) => {
  if (actionToUpdate) {
    return actionToUpdate.jsonKey;
  }

  if (schema.length > 0) {
    return schema[0].jsonKey; // loads w. empty schema
  }
  return "";
};

const resolveSortOrder = (actionToUpdate: HydratedSortAction) => {
  return actionToUpdate ? actionToUpdate.sortOrder : SortOrderKind.Ascending;
};

const AddSort: React.FC<Props> = ({
  schema,
  addAction,
  actionToUpdate,
  updateAction,
  openModal = false,
}) => {
  // For columnKey and sortOrder, use the updateAction if it exists, otherwise to default values
  const [modalIsOpen, setModalIsOpen] = useState(actionToUpdate ? openModal : false);
  const [columnJsonKey, setColumnJsonKey] = useState<string>("");
  const [sortOrder, setSortOrder] = useState<SortOrderKind>(SortOrderKind.Ascending);

  const reset = () => {
    setModalIsOpen(false);
  };

  useEffect(() => {
    setModalIsOpen(actionToUpdate ? openModal : false);
    setColumnJsonKey(resolveJsonKey(schema, actionToUpdate));
    setSortOrder(resolveSortOrder(actionToUpdate));
  }, [actionToUpdate, openModal, schema]);

  return (
    <>
      <Modal
        open={modalIsOpen}
        modalHeading="Add sort"
        primaryButtonText="Apply sort"
        secondaryButtonText="Cancel"
        aria-label="Add sort modal"
        onBlur={() => reset()}
        onRequestClose={() => reset()}
        onRequestSubmit={() => {
          const newAction = getSortAction(schema, columnJsonKey, sortOrder);

          if (actionToUpdate) {
            updateAction(actionToUpdate.idx, newAction);
            reset();
            return;
          }
          addAction(newAction);
          reset();
        }}
      >
        <div className={styles.container}>
          <div className={styles.column}>
            <Select
              id="field-selector"
              labelText="Select a field to sort by"
              value={columnJsonKey}
              onChange={e => {
                setColumnJsonKey(e.target.value);
              }}
              style={{ width: "100%" }}
            >
              <SelectItem value="" text="Select a field..." />
              {columnOptionsFromSchema(schema).map(({ value, label }) => (
                <SelectItem key={`select-opt-${value}`} text={label} value={value} />
              ))}
            </Select>
          </div>
          <div className={styles.sortOrder}>
            <Select
              id="sort-order-selector"
              value={sortOrder}
              onChange={e => {
                setSortOrder(e.target.value);
              }}
            >
              <SelectItem value="" text="Select ordering..." />
              {sortOrderOptions.map(({ value, label }) => (
                <SelectItem key={`order-${value}`} value={value} text={label} />
              ))}
            </Select>
          </div>
        </div>
      </Modal>

      <Button
        onClick={() => setModalIsOpen(true)}
        kind="ghost"
        iconDescription="Add Sort"
        renderIcon={ChevronSort16}
      >
        Add Sort
      </Button>
    </>
  );
};

export default AddSort;
